<template>
  <div class="color-picker-wrap">
    <div
      v-for="color in colors"
      :key="color"
      :style="{ background: color }"
      class="item-color pt"
      @click.stop="predefinedColorClick(color)"
    >
      <em
        v-if="selectedColor === color"
        class="w-4 h-4 bg-gray-50"
        :style="getCustomIconStyle(iconPath('check'))"
      />
    </div>
    <div class="custom-picker-wrap">
      <div
        class="custom-picker-button pt"
        @click.stop="pickerButtonClick"
      >
        <em
          v-if="customColorSelected"
          class="w-4 h-4 bg-gray-50"
          :style="getCustomIconStyle(iconPath('check'))"
        />
        <input
          ref="colorPicker"
          type="color"
          class="color-picker"
          @click.stop
          @input="setCustomColor"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import useCustomIcon from '@/utilities/composables/useCustomIcon';
import styleVariables from '@/assets/scss/variables.module.scss';

const { iconPath, getCustomIconStyle } = useCustomIcon();

const colorPicker = ref();

const props = withDefaults(
  defineProps<{
    selectedColor: string;
    colors?: string[];
  }>(),
  {
    colors: () => [
      styleVariables.pink500,
      styleVariables.error500,
      styleVariables.warning500,
      styleVariables.success500,
      styleVariables.primary500,
      styleVariables.purple500,
      styleVariables.gray500,
    ],
  }
);

const emit = defineEmits(['selectColor', 'pickerButtonClick', 'predefinedColorPick']);

const customColorSelected = computed(
  () => props.selectedColor && !props.colors.includes(props.selectedColor)
);

function pickerButtonClick() {
  colorPicker.value.value = props.selectedColor;
  colorPicker.value.click();
  emit('pickerButtonClick');
}

function selectColor(color: string) {
  emit('selectColor', color);
}

function predefinedColorClick(color: string) {
  selectColor(color);
  emit('predefinedColorPick', color);
}

function setCustomColor(event: Event) {
  const color = (event.target as HTMLInputElement).value || '#212121';
  selectColor(color);
}
</script>

<style lang="scss">
.color-picker-wrap {
  @apply flex items-center max-h-5 justify-between;

  .item-color {
    @apply flex items-center justify-center w-6 h-6 rounded-full mr-2;
  }

  .custom-picker-wrap {
    @apply border-0 border-l border-solid border-dividers-default;

    .custom-picker-button {
      background: linear-gradient(
        180deg,
        #ee46bc 0%,
        #f04438 16.67%,
        #f79009 33.33%,
        #12b76a 50%,
        #4a9aff 66.67%,
        #7a5af8 83.33%,
        #667085 100%
      );
      @apply flex justify-center items-center relative ml-2 rounded-full w-6 h-6;

      .color-picker {
        @apply absolute w-0 h-0 top-0 right-0 opacity-0;
      }
    }
  }
}
</style>
